import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from "rxjs";
import { Client, Population } from "../../../../swaggerServices";
import { ClientRouteResolver } from "../../clients/client/client-route-resolver.service";
import { PopulationRouteResolver } from "../../clients/population-taxpayers/population-route-resolver.service";
import { PopulationRulesService } from "../../clients/client/populations/population-rules/population-rules.service";
import { AbstractControl, UntypedFormGroup } from "@angular/forms";
import { PopulationService } from "../../../../swaggerServices";
import { PopulationRulesResolver } from "../../clients/client/populations/population-rules/population-rules-resolver.service";
import { PopulationClientRules } from "../../../../swaggerServices";
import { ClientCategoryWithClientQuestionsAndAnswers } from "../../../../swaggerServices/model/clientCategoryWithClientQuestionsAndAnswers";
import { Router } from "@angular/router";
import { ActivatedRoute } from "@angular/router";
import { UserInfoService } from "../../user-info.service";
import { IFormField } from "../../../shared/components/form-field-wrapper/form-field-wrapper.component";
import { TenantConfigService } from '../../../shared/services/tenant-config.service';
import { ConfigStateService } from '../../config/config-state.service';
import { TranslocoService} from "@jsverse/transloco";

declare var $: any;

interface IClient extends Client {
  populations?: Population[]
}

interface IPopulationRule extends PopulationClientRules {
  populationClientRules: ClientCategoryWithClientQuestionsAndAnswers[] // categories
}

@Component({
    selector: 'app-client-rules-sidebar',
    templateUrl: './client-rules-sidebar.component.html',
    styleUrls: ['./client-rules-sidebar.component.scss'],
    standalone: false
})
export class ClientRulesSidebarComponent implements OnInit, OnDestroy {

  subscriptions: Subscription[] = [];
  client: IClient;
  populationRules: any;
  clientPopulations: any[]; // dropdown list of all populations
  mandatoryAnsCount: string; // total count of mandatory questions which have been answered
  populationRulesFormGroup: UntypedFormGroup; // form group copied from service
  globalFieldsCount: number = 0;
  readOnly: boolean = !this.userInfo.isClientAdmin;
  uiConfig: TenantConfigService;
  yearLabel: string;

  constructor(private router: Router,
    private activatedRoute: ActivatedRoute,
    private clientRouteResolver: ClientRouteResolver,
    private config: ConfigStateService,
    private populationService: PopulationService,
    private populationRulesService: PopulationRulesService,
    private populationRouteResolver: PopulationRouteResolver,
    private populationRulesResolver: PopulationRulesResolver,
    public userInfo: UserInfoService,
    private translocoService: TranslocoService
    ) {
  }

  // get population and client data
  ngOnInit() {
    this.subscriptions.push(this.populationRulesResolver.populationRule().subscribe((data: any) => this.initPopulationRules(data)));
    this.subscriptions.push(
      this.config.loadConfig().subscribe(config => {
        this.uiConfig = new TenantConfigService(config);
        this.yearLabel = this.uiConfig.config.yearLabel;
        this.translocoService.selectTranslate('year').subscribe(value => this.yearLabel = value);
      })
    );
    this.subscriptions.push(this.clientRouteResolver.client().subscribe((client: any) => {
      this.client = client;
      // get all populations by tax year for dropdown
      this.clientPopulations = client.populations.filter(p => p.year === this.populationRules.year);
    }));

  }

  initPopulationRules(population: IPopulationRule) {
    this.populationRules = population;
    this.initMandatoryQuestions();
    this.initGlobalFieldsCount();
  }

  // get initial mandatory field count then watch for form changes and update answers count for mandatory questions
  // use timeout as new formgroup is generate on population-rules component init which occurs whenever an answer is saved
  initMandatoryQuestions() {
    this.calcMandatoryFields();
    setTimeout(() => this.populationRulesService.populationRulesFormGroup.valueChanges
      .subscribe(() => this.calcMandatoryFields()));
  }


  // counts total global answers
  initGlobalFieldsCount() {
    const globalCat = this.populationRules.populationClientRules.find(c => c.name === "Global Fields");
    if (globalCat) this.globalFieldsCount = globalCat.clientQuestions.filter(q => !q.parentId).length;
  }

  // gets the total count of answers for mandatory fields at population level and category level
  calcMandatoryFields() {
    let questionCount = 0;
    let ansCount = 0;
    this.populationRules.populationClientRules.forEach(c => c.mandatoryFieldCount = null);

    // key is id of question
    Object.keys(this.populationRulesService.populationRulesFormGroup.controls).forEach(key => {
      let questionId;
      let rowIndex: number;

      // table row questions have id format of tableQuestionId-questionId (eg 123-2). extract the table question id
      if (key.indexOf("-") !== -1) {
        questionId = Number(key.substr(0, key.indexOf("-"))); // table question id
        rowIndex = Number(key.substr(key.indexOf("-") + 1));
      } else {
        questionId = Number(key);
      }

      const q = this.populationRules.questions.find(q => q.id === questionId);

      if (q.mandatory) {
        questionCount++;
        // get all answers to required fields
        if (this.populationRulesService.populationRulesFormGroup.get(key).value)
          ansCount++;
        // mandatory not answered, append count to category level
        else
          this.populationRules.populationClientRules.forEach(c => c.clientQuestions
            .find(q => q.id === Number(key)) ? c.mandatoryFieldCount++ : false);

      }

    });

    this.mandatoryAnsCount = `${ansCount}/${questionCount}`;

  }

  // change population
  // only fetch when selecting a different population
  // check unsaved changes and show confirmation modal
  changePopulation(population: Population) {
    if (population.id === this.populationRules.id) return false;
    this.getPopulation(population.id);
  }

  getPopulation(populationId: number) {
    this.router.navigate(["clients", this.client.id, "populations", populationId, "rules"], {
      queryParams: {
        category: this.activatedRoute.snapshot.queryParams.category,
        taxYear: this.populationRules.year
      }
    });
  }

  ngAfterViewInit() {
    $('.ui.inline.dropdown').dropdown({
      action: "select", // prevent selection when clicking 'no' on confirmation popup
      preserveHTML: false,
    });
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s => s.unsubscribe()));
  }

  // gets the form control
  getFormControl: Function = (q: IFormField, rowIndex?: number): AbstractControl => {
    return rowIndex != null ? this.populationRulesService.populationRulesFormGroup.get(`${q.id}-${rowIndex}`) : this.populationRulesService.populationRulesFormGroup.controls[q.id];
  };

}
